import { useMutation } from '@apollo/client';
import React from 'react';
import Helmet from 'react-helmet';
import { MAIL_MUTATION } from '../../../../../../client/__graphql__/mutations/mail';
import { useForm } from '../../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../../utils/hooks/useFormTransition';
import { routeConfig } from '../../../../../__config__/routes';
import {
  LeadType,
  PageType
} from '../../../../../__types__/generated/globalTypes';
import { Mail, MailVariables } from '../../../../../__types__/generated/Mail';
import { Button } from '../../../../ui/button/Button';
import { ButtonGroup } from '../../../../ui/button/ButtonGroup';
import { Form, FormRow, FormWrapper } from '../../../../ui/form/Form';
import { FormError } from '../../../../ui/form/FormError';
import { Input } from '../../../../ui/form/Input';
import { ButtonLink } from '../../../../ui/link/ButtonLink';
import { Paragraph } from '../../../../ui/text/Paragraph';
import { Heading } from '../../../../ui/text/Heading';
import { Section } from '../../../../layout/Section';
import { Container } from '../../../../layout/Container';
import { Thanks } from '../../../../layout/Thanks';
import {
  GeneralValuation,
  GeneralValuationVariables
} from '../../../../../__types__/generated/GeneralValuation';
import { GENERAL_VALUATION_MUTATION } from '../../../../../../client/__graphql__/mutations/valuation';
import { createGTMEvent } from '../../../../../utils/tracking/gtm';
import { useDigtectiveContext } from '../../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const StylistOslo: React.FC = () => {
  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { submitWithDigger } = useDigtectiveContext();

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [mail, { loading: mailLoading, error: mailError }] = useMutation<
    Mail,
    MailVariables
  >(MAIL_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead && lead.mail && lead.mail.success) {
        setPosted(true);
      }
    }
  });

  const [lead, { error }] = useMutation<
    GeneralValuation,
    GeneralValuationVariables
  >(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      setLoading(false);
      if (lead?.valuation?.success) {
        setPosted(true);
        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: '1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: '00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields = getFields(fields);
      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.GENERELL
            }
          }
        });
      });
    }
  });

  return (
    <>
      <Helmet>
        <script src="https://embed.vev.page/v1/lxZ306Cpc8/ps4Qd01FrtR"></script>
      </Helmet>

      <Container>
        <FormWrapper style={fadeOut}>
          <Section className="form-section">
            <Heading tag="h2" center={true}>
              Snakk med oss om salg av eiendom
            </Heading>
            <Paragraph className="form-text" center={true}>
              Fyll ut skjema og en eiendomsmegler som kjenner ditt nærområde vil
              kontakte deg.
            </Paragraph>
            <Form onSubmit={handleSubmit} noValidate>
              {mailError &&
                mailError.graphQLErrors.map((error, index) => (
                  <FormError key={`error_${index}`} message={error.message} />
                ))}
              {fields.map((item, index) => {
                return (
                  <FormRow key={`field_${index}`}>
                    <Input
                      type={item?.type}
                      name={item?.name}
                      label={item?.label}
                      labelAfter={item?.labelAfter}
                      placeholder={item?.placeholder}
                      value={item?.value}
                      onChange={handleChange}
                      error={item?.error}
                    />
                  </FormRow>
                );
              })}

              <Paragraph className="form-text">
                Jeg anmoder PrivatMegleren om å kontakte meg på e-post og /eller
                telefon i form av sms eller oppringing.
              </Paragraph>

              <ButtonGroup>
                <Button
                  type="primary"
                  colorTheme="gold"
                  disabled={mailLoading}
                  loading={mailLoading}
                >
                  Kontakt meg
                </Button>
                <ButtonLink
                  as="a"
                  href={routeConfig?.personvern.url}
                  className="privacy-link-mobile"
                >
                  Personvernpolicy
                </ButtonLink>
              </ButtonGroup>
            </Form>
          </Section>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
            <Paragraph center={true}>
              En eiendomsmegler vil ta kontakt med deg så snart som mulig.
            </Paragraph>
          </Section>
        </Thanks>
      </Container>
    </>
  );
};

export default StylistOslo;
